import { useState, useRef, useCallback } from 'react'

import slide01 from '@/assets/intro/slide-01.jpg'
import slide02 from '@/assets/intro/slide-02.jpg'
import slide03 from '@/assets/intro/slide-03.jpg'
import slide04 from '@/assets/intro/slide-04.jpg'
import slide05 from '@/assets/intro/slide-05.jpg'
import slide06 from '@/assets/intro/slide-06.jpg'
import slide07 from '@/assets/intro/slide-07.jpg'

const slides = [
  { src: slide01, title: 'Ton espace spirituel quotidien' },
  { src: slide02, title: 'Suis ta vie de prière' },
  { src: slide03, title: 'Médite chaque jour' },
  { src: slide04, title: 'Fixe-toi des objectifs spirituels' },
  { src: slide05, title: 'Mémorise la Parole' },
  { src: slide06, title: 'Prie avec tes proches' },
  { src: slide07, title: 'Reste connecté' },
]

interface Props {
  onDone: () => void
}

export default function IntroSlider({ onDone }: Props) {
  const [current, setCurrent] = useState(0)
  const [exiting, setExiting] = useState(false)
  const startX = useRef(0)
  const startY = useRef(0)

  const isLast = current === slides.length - 1

  const dismiss = useCallback(() => {
    if (exiting) return
    setExiting(true)
    setTimeout(onDone, 300)
  }, [exiting, onDone])

  const goTo = useCallback((index: number) => {
    setCurrent(Math.max(0, Math.min(slides.length - 1, index)))
  }, [])

  const next = useCallback(() => {
    if (isLast) { dismiss(); return }
    goTo(current + 1)
  }, [current, isLast, dismiss, goTo])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX
    startY.current = e.touches[0].clientY
  }, [])

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const deltaX = startX.current - e.changedTouches[0].clientX
    const deltaY = Math.abs(e.changedTouches[0].clientY - startY.current)
    // Ignore vertical scrolls
    if (deltaY > Math.abs(deltaX)) return
    if (Math.abs(deltaX) < 50) return
    if (deltaX > 0 && current < slides.length - 1) goTo(current + 1)
    if (deltaX < 0 && current > 0) goTo(current - 1)
  }, [current, goTo])

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: '#000',
        overflow: 'hidden',
        opacity: exiting ? 0 : 1,
        transform: exiting ? 'translateY(20px)' : 'translateY(0)',
        transition: 'opacity 300ms ease, transform 300ms ease',
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Skip button — top right, dark pill for visibility on any background */}
      {!isLast && (
        <button
          onClick={onDone}
          style={{
            position: 'absolute',
            top: 'calc(env(safe-area-inset-top, 16px) + 12px)',
            right: '16px',
            zIndex: 10,
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            border: 'none',
            borderRadius: '20px',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 500,
            fontFamily: 'Inter, sans-serif',
            cursor: 'pointer',
            padding: '6px 14px',
          }}
        >
          Ignorer
        </button>
      )}

      {/* Slides — translateX animation */}
      <div
        style={{
          display: 'flex',
          width: `${slides.length * 100}%`,
          height: '100%',
          transform: `translateX(-${current * (100 / slides.length)}%)`,
          transition: 'transform 0.35s ease-out',
        }}
      >
        {slides.map((slide, i) => (
          <div
            key={i}
            style={{
              width: `${100 / slides.length}%`,
              height: '100%',
              flexShrink: 0,
            }}
          >
            <img
              src={slide.src}
              alt={slide.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
        ))}
      </div>

      {/* Bottom controls */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          paddingBottom: 'calc(env(safe-area-inset-bottom, 20px) + 16px)',
          paddingTop: '32px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
          background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
        }}
      >
        {/* Dots — 6px circles */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Slide ${i + 1}`}
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: '#fff',
                opacity: i === current ? 1 : 0.4,
                transition: 'opacity 0.3s ease',
                cursor: 'pointer',
                padding: 0,
              }}
            />
          ))}
        </div>

        {/* Next / Start button */}
        <button
          onClick={next}
          style={{
            backgroundColor: isLast ? '#fff' : 'transparent',
            color: isLast ? '#000' : '#fff',
            border: isLast ? 'none' : '1.5px solid #fff',
            borderRadius: '28px',
            padding: '12px 40px',
            fontSize: '16px',
            fontWeight: 600,
            fontFamily: 'Inter, sans-serif',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          {isLast ? 'Commencer' : 'Suivant'}
        </button>
      </div>
    </div>
  )
}
